import { useNavigate } from "react-router";

import { useSession } from "@vega-api/custom";
import { useGetAllPermissionsForCurrentUser } from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { ApolloPermission } from "@vega-api/generated/types/apolloPermission";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { isF2F, isMSW } from "@vega-core/utils/global";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type PermissionFlags = Record<ApolloPermission, boolean>;

export const usePermissionsFlags = () => {
  const { user } = useSession();

  const { data, isLoading, error } = useGetAllPermissionsForCurrentUser({
    query: {
      enabled: isTruthy(user),
    },
  });
  const toast = useToast();

  const permissions = data?.entries;

  const evaluatePermission = (permissionName: ApolloPermission) => {
    if (isMSW && !isF2F) return true;

    if (isFalsy(permissions)) return false;

    return permissions.some(
      (permission) =>
        permission.permissionName === permissionName &&
        isTruthy(permission.permissionEnabled),
    );
  };

  const permissionFlags = Object.values(ApolloPermission).reduce(
    (acc, permissionName) => {
      acc[permissionName] = evaluatePermission(permissionName);

      return acc;
    },
    {} as PermissionFlags,
  );

  const navigate = useNavigate();

  const navigateBack = () => {
    toast({
      title: m.helpCentre_permissions_noEditAccess(),
      type: "error",
    });
    navigate(-1);
  };

  return {
    permissionFlags,
    navigateBack,
    isLoading,
    error,
  };
};
